import React from "react";
import { Box, Button, IconButton, LinearProgress, Typography } from "@mui/material";
import { PushPinOutlined } from "@mui/icons-material";
import { Link as RouterLink } from "react-router-dom";
import { SPORT_BY_ID } from "../../constants/sportWorkspace";
import { SPORT_ICONS } from "../sportIcons";
import { CompareHeadshotRing } from "../compare/comparePlayerVisuals";
import { homeCardHover } from "../home/homeStyles";
import {
  fanHubOverlineSx,
  fanHubPaperSx,
  sportAccent,
} from "./fanHubStyles";
import { suggestionKeyForCompare } from "../../utils/fanHubProfile";
import { brandOutlinedButtonSx, brandRgba } from "../../utils/brandAssets";

export const FanHubLineupGrid = ({ lineups = [] }) => (
  <Box sx={{ mb: 3 }}>
    <Typography variant="overline" sx={fanHubOverlineSx}>
      What I’ve built
    </Typography>
    <Typography variant="h5" fontWeight={800} sx={{ mt: 0.25, mb: 2 }}>
      Lineups
    </Typography>
    <Box
      sx={{
        display: "grid",
        gap: 1.5,
        gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
      }}
    >
      {lineups.map((lineup) => {
        const accent = sportAccent(lineup.sport);
        const Icon = SPORT_ICONS[lineup.sport];
        const pct = lineup.slots
          ? Math.round((lineup.filled / lineup.slots) * 100)
          : 0;
        return (
          <Box
            key={lineup.sport}
            component={RouterLink}
            to={lineup.href}
            sx={{
              ...fanHubPaperSx(accent),
              ...homeCardHover,
              p: 2,
              textDecoration: "none",
              color: "inherit",
              display: "block",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
              {Icon && <Icon sx={{ color: accent }} />}
              <Typography fontWeight={800}>
                {SPORT_BY_ID[lineup.sport]?.label} lineup
              </Typography>
            </Box>
            <Typography variant="h4" fontWeight={800} sx={{ color: accent }}>
              {lineup.filled}
              <Typography component="span" color="text.secondary" variant="body1">
                /{lineup.slots}
              </Typography>
            </Typography>
            <LinearProgress
              variant="determinate"
              value={pct}
              sx={{
                mt: 1.25,
                height: 6,
                borderRadius: 99,
                bgcolor: "rgba(255,255,255,0.06)",
                "& .MuiLinearProgress-bar": { bgcolor: accent },
              }}
            />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1.25 }}>
              {pct === 0 ? "Start building" : "Keep editing"}
            </Typography>
          </Box>
        );
      })}
    </Box>
  </Box>
);

export const FanHubComparisons = ({
  comparisons = [],
  suggestions = [],
  onAdd,
  onPin,
  onRemove,
  isPinning,
}) => (
  <Box sx={{ mb: 4 }}>
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-end",
        mb: 1.5,
      }}
    >
      <Box>
        <Typography variant="overline" sx={fanHubOverlineSx}>
          Debates
        </Typography>
        <Typography variant="h5" fontWeight={800} sx={{ mt: 0.25 }}>
          Saved comparisons
        </Typography>
      </Box>
      <Button onClick={onAdd} size="small" variant="outlined" sx={brandOutlinedButtonSx}>
        New comparison
      </Button>
    </Box>
    {comparisons.length === 0 ? (
      <Box sx={{ ...fanHubPaperSx(), p: 3 }}>
        <Typography fontWeight={700}>No debates saved yet</Typography>
        <Typography color="text.secondary" sx={{ mt: 0.75 }}>
          Pick two names, save the matchup, reopen it in one tap.
        </Typography>
      </Box>
    ) : (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
        {comparisons.map((row) => {
          const accent = sportAccent(row.sport);
          const suggestion = suggestions.find(
            (item) =>
              item.key === suggestionKeyForCompare(row.sport, row.playerIds)
          );
          const left = row.players?.[0];
          const right = row.players?.[1];
          return (
            <Box
              key={row.id}
              sx={{
                ...fanHubPaperSx(accent),
                p: 1.75,
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                flexWrap: "wrap",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.25,
                  minWidth: 0,
                  flex: 1,
                }}
              >
                <CompareHeadshotRing
                  playerId={left?.playerId}
                  sport={row.sport}
                  size={44}
                  accent={accent}
                />
                <Typography fontWeight={800} noWrap>
                  {left?.name}
                </Typography>
                <Typography
                  sx={{
                    color: accent,
                    fontWeight: 800,
                    letterSpacing: 1,
                    px: 0.5,
                  }}
                >
                  VS
                </Typography>
                <Typography fontWeight={800} noWrap>
                  {right?.name}
                </Typography>
                <CompareHeadshotRing
                  playerId={right?.playerId}
                  sport={row.sport}
                  size={44}
                  accent={accent}
                />
              </Box>
              <Button component={RouterLink} to={row.href} size="small">
                Open
              </Button>
              {suggestion && !suggestion.alreadyPinned && (
                <IconButton
                  aria-label="Pin to dashboard"
                  disabled={isPinning}
                  onClick={() => onPin(suggestion)}
                >
                  <PushPinOutlined />
                </IconButton>
              )}
              <Button size="small" color="inherit" onClick={() => onRemove(row.id)}>
                Remove
              </Button>
            </Box>
          );
        })}
      </Box>
    )}
  </Box>
);

export const FanHubPublished = ({ rosters = [] }) => {
  if (!rosters.length) return null;
  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="overline" sx={fanHubOverlineSx}>
        Community
      </Typography>
      <Typography variant="h6" fontWeight={800} sx={{ mb: 1.25 }}>
        Published rosters
      </Typography>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {rosters.map((roster) => {
          const accent = sportAccent(roster.sport);
          return (
            <Box
              key={roster.id || roster.slug}
              sx={{
                ...fanHubPaperSx(accent),
                p: 1.75,
                borderLeft: `3px solid ${brandRgba(accent, 0.7)}`,
              }}
            >
              <Typography fontWeight={700}>{roster.title}</Typography>
              <Typography variant="caption" sx={{ color: accent }}>
                {SPORT_BY_ID[roster.sport]?.label} · Published
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};
